import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQueryClient,
} from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getListForFilesImport } from '../utils/urls';

import { ImportFileAccountList } from 'types/accounts';
import { ErrorModal } from 'types/modalTypes';

const pageLength = 20;

async function fetchAccountList({
  searchKey,
  start,
  pageLength,
  accountId,
}: TVariables): Promise<TResult> {
  const response = await fetch(
    getListForFilesImport({
      accountId,
      start,
      pageLength,
      searchKey,
    }),
    {
      method: 'GET',
      headers: {
        Authorization: getTokenWithType(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  );
  return response.json();
}

const debouncedFetch = pDebounce(fetchAccountList, 500);

type TError = ErrorModal;

type TResult = {
  Data: Array<ImportFileAccountList>;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  accountId: number;
  start: number;
  pageLength: number;
  searchKey: string;
};

function useGetAccountsListForImportFiles(
  accountId: number,
  searchKey: string,
): UseInfiniteQueryResult<TResult, TError> {
  const queryClient = useQueryClient();
  const data: any | undefined = queryClient.getQueryData([
    'get_accounts_list_for_import_files',
    accountId,
    searchKey,
  ]);

  const start = data && data.pages ? data.pages.length * pageLength : 0;
  return useInfiniteQuery(
    ['get_accounts_list_for_import_files', accountId, searchKey],
    () => {
      // if (!accountId) return;
      return debouncedFetch({
        accountId,
        searchKey,
        start,
        pageLength,
      });
    },
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetAccountsListForImportFiles };
